import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Layout } from '../components/Layout';
import { BetaModal } from '../components/BetaModal';
import { useAuthStore } from '../stores/auth.store';
import { useMounted } from '../hooks/useMounted';
import { useUILocale } from '../hooks/useUILocale';
import { useHreflangHead } from '../hooks/useHreflangHead';
import { withLocale } from '../locale';

const LandingPage: React.FC = () => {
  const [showModal, setShowModal] = useState(false);
  const mounted = useMounted();
  const isAuthenticatedRaw = useAuthStore((state) => state.isAuthenticated);
  const userRaw = useAuthStore((state) => state.user);
  const isAuthenticated = mounted && isAuthenticatedRaw;
  const user = mounted ? userRaw : null;
  const uiLocale = useUILocale();
  const href = (p: string) => withLocale(uiLocale, p);
  useHreflangHead(uiLocale, '/');

  return (
    <Layout>
      <div className="flex flex-col items-center justify-center min-h-[80vh] text-center">
        <span className="text-8xl mb-6">📖</span>
        <h1 className="text-4xl font-bold text-gray-900 mb-4">StoryChain</h1>
        <p className="text-xl text-gray-600 mb-8 max-w-sm">
          Create stories together with friends, one sentence at a time
        </p>

        <div className="bg-white rounded-2xl p-6 shadow-lg w-full max-w-sm">
          <h2 className="font-semibold text-lg mb-4">How it works</h2>
          <div className="space-y-3 text-left text-gray-600">
            <div className="flex gap-3">
              <span className="text-xl">1️⃣</span>
              <p>Start a story in the app</p>
            </div>
            <div className="flex gap-3">
              <span className="text-xl">2️⃣</span>
              <p>Share link with friends</p>
            </div>
            <div className="flex gap-3">
              <span className="text-xl">3️⃣</span>
              <p>Each person adds one sentence</p>
            </div>
            <div className="flex gap-3">
              <span className="text-xl">4️⃣</span>
              <p>AI creates the final story!</p>
            </div>
          </div>
        </div>

        <div className="mt-8 space-y-3 w-full max-w-sm">
          {isAuthenticated && (
            <div className="bg-green-50 border border-green-200 rounded-xl p-4 text-left">
              <p className="text-green-800 font-medium">
                ✓ Signed in as {user?.name || user?.email}
              </p>
              <Link
                to={href('/library')}
                className="inline-block mt-2 text-green-800 underline text-sm"
              >
                Go to My Library →
              </Link>
            </div>
          )}
          <Link
            to={href('/explore')}
            className="block w-full bg-primary-600 text-white py-3 px-6 rounded-xl font-semibold text-center hover:bg-primary-700 transition"
          >
            Explore Stories & Comics
          </Link>
          <button
            onClick={() => setShowModal(true)}
            className="block w-full bg-white text-primary-600 py-3 px-6 rounded-xl font-semibold text-center border border-primary-200 hover:bg-primary-50 transition"
          >
            Download the App
          </button>
          {!isAuthenticated && (
            <Link
              to={href('/login')}
              className="block w-full text-primary-600 py-2 px-6 rounded-xl font-medium text-center hover:bg-primary-50 transition"
            >
              Sign in on the web
            </Link>
          )}
          <p className="text-sm text-gray-500">
            Got a story link? Just open it to join!
          </p>
        </div>
        <BetaModal isOpen={showModal} onClose={() => setShowModal(false)} />
      </div>

      <footer className="mt-12 pb-8 text-center">
        <div className="flex items-center justify-center gap-4 text-sm text-gray-400">
          <Link to={href('/support')} className="hover:text-gray-600 transition">Support</Link>
          <span>•</span>
          <Link to={href('/privacy')} className="hover:text-gray-600 transition">Privacy</Link>
          <span>•</span>
          <Link to={href('/terms')} className="hover:text-gray-600 transition">Terms</Link>
        </div>
      </footer>
    </Layout>
  );
};

export default LandingPage;
